import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { ApiError } from '../../api/client.js';
import {
  useAssignInvoice,
  useComments,
  useDisputeInvoice,
  useInvoice,
  openArtifact,
} from '../../api/invoices.js';
import type { DisputeReason } from '../../i18n/dispute-reasons.js';
import { useUserByUid } from '../../api/users.js';
import type { DisputeResolution, Invoice, Membership } from '../../api/types.js';
import { StateBadge } from './StateBadge.js';
import { CommentsThread } from './CommentsThread.js';
import { ActivityTimeline } from './ActivityTimeline.js';
import { useIsMobile } from '../../lib/breakpoints.js';
import { AssignModal } from './AssignModal.js';
import { DisputeModal } from './DisputeModal.js';
import { RequestValidationModal } from './RequestValidationModal.js';
import { useRequestValidation } from '../../api/validations.js';
import { formatAmount, formatDueChip, formatInvoiceDate, formatRelative } from './format.js';
import { resolveDueDate } from './dueDate.js';
import { describeOutcome } from './disputeOutcome.js';

const AP_ROLES = new Set<Membership['role']>([
  'ap_clerk',
  'ap_approver',
  'ap_admin',
  'group_admin',
]);

// REQ-FE-033 area A1 — `DIRECTION_LABEL_FR` map deleted with the
// eyebrow it used to feed. The invoice ID h1 is sufficient context
// per mockup `03-invoice-list.png`.

// REQ-FE-032 — Pass-5 detail action bar copy. Mockup `03-invoice-list.png`
// shows 5 short labels + a single-letter kbd chip + overflow. The mutations
// behind Accuser / Traiter / Rejeter don't exist on the contract yet — those
// buttons render disabled with a "bientôt disponible" tooltip until BE
// ships them (REQ-BE-N flagged in REPORT).
const ACTION_DEFERRED_TITLE = 'Bientôt disponible (S1)';

interface Props {
  tenantId: string;
  tenantName: string;
  role?: Membership['role'] | null;
  // 'standalone' (default): standalone detail route — renders Crumbs at
  //   the top and the inline ActivityTimeline card at the bottom.
  // 'centre': rendered as the centre pane of the split-pane invoice list.
  //   Skips Crumbs (the list already has the page heading) and the
  //   inline ActivityTimeline card (it's lifted to the right rail via
  //   `<InvoiceDetailRail>`).
  paneMode?: 'standalone' | 'centre';
  // In 'centre' pane mode, the primaryKey comes from the parent rather
  // than `useParams` (`useParams` may still resolve from URL, but passing
  // explicitly keeps the contract obvious).
  primaryKey?: string;
}

export function InvoiceDetail({
  tenantId,
  tenantName,
  role = null,
  paneMode = 'standalone',
  primaryKey: pkProp,
}: Props) {
  const { primaryKey: raw } = useParams<{ primaryKey: string }>();
  const primaryKey = pkProp ?? raw ?? '';
  const query = useInvoice(primaryKey);

  if (query.isLoading) {
    return <DetailSkeleton tenantId={tenantId} tenantName={tenantName} />;
  }

  if (query.isError) {
    const status = query.error instanceof ApiError ? query.error.status : 0;
    if (status === 404) {
      return (
        <ErrorState
          tenantId={tenantId}
          title="Facture introuvable"
          message="Aucune facture ne correspond à cet identifiant."
        />
      );
    }
    if (status === 403) {
      return (
        <ErrorState
          tenantId={tenantId}
          title="Accès refusé"
          message="Vous n'avez pas accès à cette facture."
        />
      );
    }
    return (
      <ErrorState
        tenantId={tenantId}
        title="Erreur de chargement"
        message="Une erreur inattendue est survenue. Réessayez dans un instant."
      />
    );
  }

  const invoice = query.data!;
  return (
    <DetailBody
      invoice={invoice}
      tenantId={tenantId}
      tenantName={tenantName}
      role={role}
      paneMode={paneMode}
    />
  );
}

function Crumbs({
  tenantId,
  tenantName,
  invoiceNumber,
}: {
  tenantId: string;
  tenantName: string;
  invoiceNumber: string;
}) {
  return (
    <nav className="invoice-detail__crumbs" aria-label="Fil d'Ariane">
      <span className="muted">{tenantName}</span>
      <span className="muted invoice-detail__crumb-sep">/</span>
      <Link to={`/t/${tenantId}/invoices`} className="invoice-detail__crumb-link">
        Inbox
      </Link>
      <span className="muted invoice-detail__crumb-sep">/</span>
      <span>{invoiceNumber}</span>
    </nav>
  );
}

function ErrorState({
  tenantId,
  title,
  message,
}: {
  tenantId: string;
  title: string;
  message: string;
}) {
  return (
    <section className="invoice-detail" data-testid="invoice-detail-error">
      <h1 className="display invoice-detail__title">{title}</h1>
      <p className="muted">{message}</p>
      <Link to={`/t/${tenantId}/invoices`} className="btn-primary">
        ← Retour à l&apos;inbox
      </Link>
    </section>
  );
}

function DetailSkeleton({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  return (
    <section className="invoice-detail" data-testid="invoice-detail-skeleton">
      <Crumbs tenantId={tenantId} tenantName={tenantName} invoiceNumber="…" />
      <div className="invoice-detail__card invoice-detail__card--skeleton">
        <div className="ph-line" style={{ width: '40%' }} />
        <div className="ph-line" style={{ width: '60%' }} />
        <div className="ph-line" style={{ width: '30%' }} />
      </div>
      <div className="invoice-detail__card invoice-detail__card--skeleton">
        <div className="ph-line" style={{ width: '70%' }} />
        <div className="ph-line" style={{ width: '50%' }} />
      </div>
    </section>
  );
}

function DetailBody({
  invoice,
  tenantId,
  tenantName,
  role,
  paneMode,
}: {
  invoice: Invoice;
  tenantId: string;
  tenantName: string;
  role: Membership['role'] | null;
  paneMode: 'standalone' | 'centre';
}) {
  const isCentrePane = paneMode === 'centre';
  const due = resolveDueDate(invoice.dueDate, invoice.invoiceDate);
  const invoiceNumber = invoice.invoiceNumber ?? '(sans numéro)';
  const hasReadable = invoice.artifacts.readablePdf != null;
  const hasSigned = invoice.artifacts.signedFile != null;
  const hasSource = invoice.artifacts.source != null;
  const attachments = invoice.artifacts.attachments ?? [];

  const canAssign = role != null && AP_ROLES.has(role);
  const canDispute = role != null && AP_ROLES.has(role);
  const canRequestValidation = role != null && AP_ROLES.has(role);
  const alreadyDisputed = invoice.dispute != null;
  const [assignOpen, setAssignOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [rvOpen, setRvOpen] = useState(false);
  const isMobile = useIsMobile();
  const assignMutation = useAssignInvoice(invoice.primaryKey);
  const disputeMutation = useDisputeInvoice(invoice.primaryKey);
  const rvMutation = useRequestValidation(invoice.primaryKey);
  const [assignToast, setAssignToast] = useState<string | null>(null);
  const [disputeError, setDisputeError] = useState<string | null>(null);
  const [rvError, setRvError] = useState<string | null>(null);

  async function pickAssignee(uid: string | null) {
    setAssignToast(null);
    try {
      await assignMutation.mutateAsync(uid);
      setAssignOpen(false);
      setAssignToast(uid ? 'Facture assignée.' : 'Assignation retirée.');
    } catch {
      setAssignToast("Échec de l'assignation.");
    }
  }

  async function submitDispute(payload: { reason: DisputeReason; comment: string }) {
    setDisputeError(null);
    setAssignToast(null);
    try {
      await disputeMutation.mutateAsync(payload);
      setDisputeOpen(false);
      setAssignToast('Litige soumis au fournisseur.');
    } catch {
      setDisputeError('Échec de la soumission du litige.');
    }
  }

  async function submitRequestValidation(payload: {
    validatorUid: string;
    requestComment: string | null;
  }) {
    setRvError(null);
    setAssignToast(null);
    try {
      await rvMutation.mutateAsync(payload);
      setRvOpen(false);
      setAssignToast('Demande de validation envoyée.');
    } catch (e) {
      // 409 → already pending for this validator on this invoice
      const status = (e as { status?: number })?.status;
      setRvError(
        status === 409
          ? 'Une demande de validation est déjà en attente pour ce validateur.'
          : "Échec de l'envoi de la demande de validation.",
      );
    }
  }

  // REQ-FE-032: "1 NOTE INTERNE" chip on the header — derive from comments
  // query (cheap, already cached by other code paths).
  const commentsQ = useComments(invoice.primaryKey);
  const noteCount = commentsQ.data?.length ?? 0;
  const dueChip = formatDueChip(due.iso);
  const supplierCity = null; // REQ-BE-N: Invoice contract has no `supplierCity` field today.

  return (
    <section
      className={`invoice-detail${isCentrePane ? ' invoice-detail--pane' : ''}`}
      aria-label={`Détail facture ${invoiceNumber}`}
    >
      {isCentrePane ? null : (
        <Crumbs tenantId={tenantId} tenantName={tenantName} invoiceNumber={invoiceNumber} />
      )}

      {/* REQ-FE-033 area A1+A2+A3 — header row: invoice ID h1 + status
          + format + notes chips inline left, action toolbar inline-flex
          right-aligned via `justify-content: space-between` so the
          whole header is one tight line. The eyebrow "Facture reçue"
          (FE-032) is dropped — the invoice ID is enough context. */}
      <header className="invoice-detail__head" data-testid="invoice-detail-head">
        <div className="invoice-detail__head-title">
          <h1 className="display invoice-detail__title" data-testid="invoice-detail-title">
            {invoiceNumber}
          </h1>
          <StateBadge state={invoice.state} dispute={invoice.dispute} />
          {/* REQ-BE-N: Invoice contract has no `format` field. When BE
              ships it, render the FACTUR-X chip conditionally on
              `format === 'FACTURX' || format === 'UBL' || format === 'CII'`. */}
          {noteCount > 0 ? (
            <span
              className="pill pill--alert"
              data-testid="invoice-detail-notes-chip"
              title={`${noteCount} note${noteCount > 1 ? 's' : ''} interne${noteCount > 1 ? 's' : ''}`}
            >
              {noteCount} NOTE{noteCount > 1 ? 'S' : ''} INTERNE{noteCount > 1 ? 'S' : ''}
            </span>
          ) : null}
        </div>
        {/* Action toolbar inline-flex right-aligned. 5 buttons +
            overflow. Mutations behind Accuser / Traiter / Rejeter
            aren't on the contract today (REQ-BE-N flagged) — those
            render disabled with a "bientôt" tooltip. */}
        <div className="invoice-detail__actions" role="toolbar" aria-label="Actions">
          <button
            type="button"
            className="btn-ghost"
            disabled
            title={ACTION_DEFERRED_TITLE}
            data-testid="action-acknowledge"
          >
            Accuser
            <kbd className="action-kbd" aria-hidden="true">
              e
            </kbd>
          </button>
          <button
            type="button"
            className="btn-ghost"
            disabled
            title={ACTION_DEFERRED_TITLE}
            data-testid="action-process"
          >
            Traiter
            <kbd className="action-kbd" aria-hidden="true">
              p
            </kbd>
          </button>
          {alreadyDisputed ? (
            <span
              className="pill pill--warning"
              data-testid="action-dispute-badge"
              title="Cette facture est déjà en litige"
            >
              Litige soumis
            </span>
          ) : (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setDisputeOpen(true)}
              disabled={!canDispute || disputeMutation.isPending}
              title={canDispute ? 'Soumettre un litige au fournisseur' : 'Lecture seule'}
              data-testid="action-dispute"
            >
              Litige
              <kbd className="action-kbd" aria-hidden="true">
                d
              </kbd>
            </button>
          )}
          <button
            type="button"
            className="btn-ghost"
            disabled
            title={ACTION_DEFERRED_TITLE}
            data-testid="action-reject"
          >
            Rejeter
            <kbd className="action-kbd" aria-hidden="true">
              r
            </kbd>
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setRvOpen(true)}
            disabled={!canRequestValidation || rvMutation.isPending}
            title={canRequestValidation ? 'Demander une validation à un employé' : 'Lecture seule'}
            data-testid="action-validation"
          >
            Validation
            <kbd className="action-kbd" aria-hidden="true">
              v
            </kbd>
          </button>
          {/* Overflow menu — keeps existing actions reachable + testid-stable
            (action-assign / action-print / artifact-*) while the visible
            row stays mockup-faithful. */}
          <details className="invoice-detail__overflow" data-testid="invoice-detail-overflow">
            <summary
              className="btn-ghost invoice-detail__overflow-toggle"
              aria-label="Plus d'actions"
              data-testid="invoice-detail-overflow-toggle"
            >
              <MoreHorizontal aria-hidden="true" size={16} strokeWidth={1.5} />
            </summary>
            <div className="invoice-detail__overflow-panel">
              <button
                type="button"
                className="invoice-detail__overflow-item"
                onClick={() => setAssignOpen(true)}
                disabled={!canAssign || assignMutation.isPending}
                title={canAssign ? 'Assigner cette facture' : 'Lecture seule'}
                data-testid="action-assign"
              >
                {invoice.assignedTo ? 'Réassigner' : 'Assigner'}
                <kbd className="action-kbd" aria-hidden="true">
                  a
                </kbd>
              </button>
              {isMobile ? null : (
                <button
                  type="button"
                  className="invoice-detail__overflow-item"
                  onClick={() => window.print()}
                  data-testid="action-print"
                >
                  Imprimer
                </button>
              )}
              <div className="invoice-detail__overflow-section" aria-label="Téléchargements">
                <ArtifactButton
                  primaryKey={invoice.primaryKey}
                  artifact="readable"
                  label="Télécharger PDF"
                  enabled={hasReadable}
                />
                <ArtifactButton
                  primaryKey={invoice.primaryKey}
                  artifact="source"
                  label="Télécharger source"
                  enabled={hasSource}
                />
                <ArtifactButton
                  primaryKey={invoice.primaryKey}
                  artifact="signed"
                  label="Télécharger signé"
                  enabled={hasSigned}
                />
                {attachments.length > 0 ? (
                  <AttachmentsList primaryKey={invoice.primaryKey} count={attachments.length} />
                ) : null}
              </div>
            </div>
          </details>
        </div>
      </header>

      {/* REQ-FE-033 area A — supplier + assignment sub-lines now live
          below the unified header row (chips + actions). */}
      <div className="invoice-detail__supplier-line" data-testid="invoice-detail-supplier-line">
        <span className="invoice-detail__supplier-name">{invoice.sender?.name ?? '—'}</span>
        {invoice.sender?.siren ? (
          <>
            <span className="invoice-detail__supplier-sep" aria-hidden="true">
              ·
            </span>
            <span className="muted">SIREN {invoice.sender.siren}</span>
          </>
        ) : null}
      </div>
      <div className="invoice-detail__assignment" data-testid="invoice-detail-assignment">
        <span className="muted">Assignée à</span>{' '}
        <span data-testid="meta-assignedTo">
          <AssigneeName uid={invoice.assignedTo ?? null} />
        </span>
        {canAssign ? (
          <>
            {' · '}
            <button
              type="button"
              className="invoice-detail__assignment-change"
              onClick={() => setAssignOpen(true)}
              data-testid="action-assign-change"
              disabled={assignMutation.isPending}
            >
              Changer
            </button>
          </>
        ) : null}
      </div>

      {/* REQ-FE-032 area A4 — FOURNISSEUR card (Raison sociale / SIREN /
          Ville). Ville isn't on the contract — show "—" + flag. */}
      <article className="invoice-detail__card" data-testid="invoice-detail-supplier">
        <div className="eyebrow">Fournisseur</div>
        <dl className="invoice-detail__kv">
          <div>
            <dt>Raison sociale</dt>
            <dd>{invoice.sender?.name ?? '—'}</dd>
          </div>
          <div>
            <dt>SIREN</dt>
            <dd>{invoice.sender?.siren ?? '—'}</dd>
          </div>
          <div>
            <dt>Ville</dt>
            <dd className="muted" data-testid="invoice-detail-supplier-city">
              {supplierCity ?? '—'}
            </dd>
          </div>
        </dl>
      </article>

      {/* REQ-FE-032 area A5 — RÉFÉRENCES card (Référence / Bon de commande
          / Date d'émission / Date d'échéance with overdue chip). */}
      <article className="invoice-detail__card" data-testid="invoice-detail-references">
        <div className="eyebrow">Références</div>
        <dl className="invoice-detail__kv">
          <div>
            <dt>Référence facture</dt>
            <dd>{invoice.invoiceNumber ?? '—'}</dd>
          </div>
          <div>
            <dt>Bon de commande</dt>
            <dd className="muted" data-testid="invoice-detail-purchase-order">
              {/* REQ-BE-N: Invoice contract has no purchaseOrder field. */}—
            </dd>
          </div>
          <div>
            <dt>Date d&apos;émission</dt>
            <dd>{formatInvoiceDate(invoice.invoiceDate)}</dd>
          </div>
          <div>
            <dt>Date d&apos;échéance</dt>
            <dd>
              {due.iso ? formatInvoiceDate(due.iso) : '—'}
              {due.isEstimated ? (
                <span className="invoice-detail__estimated" data-testid="due-estimated">
                  estimée
                </span>
              ) : null}
              {dueChip.tone !== 'unknown' && dueChip.tone !== 'later' ? (
                <span
                  className={`invoice-detail__due-chip invoice-detail__due-chip--${dueChip.tone}`}
                  data-testid="invoice-detail-due-chip"
                >
                  {dueChip.label}
                </span>
              ) : null}
            </dd>
          </div>
        </dl>
      </article>

      {/* REQ-FE-032 area A6 — MONTANTS card. HT + TVA breakdown isn't on
          the contract — show "—" + flag. Total TTC renders bold. */}
      <article className="invoice-detail__card" data-testid="invoice-detail-amounts">
        <div className="eyebrow">Montants</div>
        <dl className="invoice-detail__kv">
          <div>
            <dt>Total HT</dt>
            <dd className="muted" data-testid="invoice-detail-total-ht">
              {/* REQ-BE-N: Invoice contract has no totalAmountHT field. */}—
            </dd>
          </div>
          <div>
            <dt>TVA</dt>
            <dd className="muted" data-testid="invoice-detail-vat">
              {/* REQ-BE-N: Invoice contract has no vat / vatBreakdown field. */}—
            </dd>
          </div>
          <div>
            <dt>Total TTC</dt>
            <dd className="invoice-detail__amount-bold" data-testid="detail-amount">
              {formatAmount(invoice.amountTotal, invoice.currency)}
            </dd>
          </div>
        </dl>
      </article>

      {/* Dispute panel — kept inline when the invoice is in litigation. */}
      {invoice.dispute ? (
        <article
          className="invoice-detail__card invoice-detail__dispute"
          data-testid="detail-dispute"
        >
          <div className="eyebrow" style={{ color: '#8A4B00' }}>
            En litige
          </div>
          <div className="invoice-detail__dispute-reason">{invoice.dispute.reason}</div>
          <div className="invoice-detail__dispute-comment">{invoice.dispute.comment}</div>
          <div className="muted">
            Par {invoice.dispute.byDisplayName ?? invoice.dispute.byEmail ?? invoice.dispute.byUid}
            {' · '}
            {formatRelative(invoice.dispute.requestedAt)}
          </div>
          {invoice.dispute.resolution ? (
            <DisputeResolutionPanel resolution={invoice.dispute.resolution} />
          ) : null}
        </article>
      ) : null}

      {/* REQ-FE-032 area A10 — NOTES INTERNES card. Renamed from
          "Commentaires internes". Privacy hint + composer copy live
          inside CommentsThread (separate PR if needed). */}
      <article className="invoice-detail__card invoice-detail__notes">
        <div className="invoice-detail__notes-head">
          <div className="eyebrow">Notes internes</div>
          <span className="muted invoice-detail__notes-privacy">
            Privé Altyor — ne quitte pas la plateforme
          </span>
        </div>
        <CommentsThread primaryKey={invoice.primaryKey} />
        <p
          className="muted invoice-detail__notes-footnote"
          data-testid="invoice-detail-notes-footnote"
        >
          Distinct du commentaire de litige (qui, lui, part vers le fournisseur).
        </p>
      </article>

      {/* Activity timeline — in centre-pane mode this lives in the right rail
          (see <InvoiceDetailRail>); skip the inline card to avoid duplication. */}
      {isCentrePane ? null : (
        <article className="invoice-detail__card">
          <div className="eyebrow">Historique</div>
          <ActivityTimeline
            primaryKey={invoice.primaryKey}
            invoiceCreatedAt={invoice.receivedAt ?? null}
          />
        </article>
      )}

      {/* Print-only footer with app + invoice cross-ref. Hidden on screen. */}
      <div className="invoice-detail__print-footer" aria-hidden="true">
        <span>Altyor Invoicing · {new Date().toISOString().slice(0, 10)}</span>
        <span> · Réf. interne </span>
        <code>{invoice.primaryKey}</code>
      </div>

      {assignToast ? (
        <div className="invoice-detail__toast" role="status" data-testid="assign-toast">
          {assignToast}
        </div>
      ) : null}

      {assignOpen ? (
        <AssignModal
          tenantId={tenantId}
          currentAssigneeUid={invoice.assignedTo ?? null}
          onPick={pickAssignee}
          onClose={() => setAssignOpen(false)}
          busy={assignMutation.isPending}
        />
      ) : null}

      {disputeOpen ? (
        <>
          <DisputeModal
            invoiceNumber={invoiceNumber}
            supplierName={invoice.sender?.name ?? null}
            amountTotal={invoice.amountTotal ?? null}
            currency={invoice.currency ?? null}
            busy={disputeMutation.isPending}
            onSubmit={submitDispute}
            onClose={() => setDisputeOpen(false)}
          />
          {disputeError ? (
            <div
              className="invoice-detail__toast invoice-detail__toast--error"
              role="alert"
              data-testid="dispute-error"
            >
              {disputeError}
            </div>
          ) : null}
        </>
      ) : null}

      {rvOpen ? (
        <>
          <RequestValidationModal
            invoiceNumber={invoiceNumber}
            tenantId={tenantId}
            busy={rvMutation.isPending}
            onSubmit={submitRequestValidation}
            onClose={() => setRvOpen(false)}
          />
          {rvError ? (
            <div
              className="invoice-detail__toast invoice-detail__toast--error"
              role="alert"
              data-testid="rv-error"
            >
              {rvError}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function DisputeResolutionPanel({ resolution }: { resolution: DisputeResolution }) {
  const o = describeOutcome(resolution.outcome);
  return (
    <div className="invoice-detail__dispute-resolution" data-testid="dispute-resolution">
      <div className="eyebrow">Résolution</div>
      <div className="invoice-detail__dispute-resolution-line">
        <span className={`pill ${o.pillClass}`} data-testid="dispute-resolution-pill">
          {o.labelFr}
        </span>
        <span className="muted">· le {formatInvoiceDate(resolution.at)}</span>
      </div>
      <dl className="invoice-detail__dispute-resolution-meta">
        <div>
          <dt>Référence Iopole</dt>
          <dd data-testid="dispute-resolution-iopole">{resolution.iopoleStatusId ?? '—'}</dd>
        </div>
        <div>
          <dt>Action par</dt>
          <dd data-testid="dispute-resolution-by">{resolution.by ?? 'Système Iopole'}</dd>
        </div>
      </dl>
    </div>
  );
}

function AssigneeName({ uid }: { uid: string | null }) {
  const lookup = useUserByUid(uid);
  if (!uid) return <>—</>;
  if (lookup.isLoading) return <span className="muted">…</span>;
  const u = lookup.data;
  if (!u) return <span className="invoice-detail__iopole-id">{uid}</span>;
  return <>{u.displayName ?? u.email ?? uid}</>;
}

function ArtifactButton({
  primaryKey,
  artifact,
  label,
  enabled,
}: {
  primaryKey: string;
  artifact: string;
  label: string;
  enabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!enabled) {
    return (
      <button
        type="button"
        className="btn-ghost"
        disabled
        title="Fichier indisponible"
        data-testid={`artifact-${artifact}`}
      >
        {label}
      </button>
    );
  }
  async function handleClick() {
    setBusy(true);
    setError(null);
    try {
      await openArtifact(primaryKey, artifact);
    } catch {
      setError('Téléchargement impossible.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="invoice-detail__artifact">
      <button
        type="button"
        className="btn-primary"
        onClick={handleClick}
        disabled={busy}
        data-testid={`artifact-${artifact}`}
      >
        {busy ? 'Ouverture…' : label}
      </button>
      {error ? <span className="invoice-detail__artifact-error">{error}</span> : null}
    </span>
  );
}

function AttachmentsList({ primaryKey, count }: { primaryKey: string; count: number }) {
  return (
    <details className="invoice-detail__attachments" data-testid="artifact-attachments">
      <summary>Pièces jointes ({count})</summary>
      <div className="invoice-detail__attachments-list">
        {Array.from({ length: count }).map((_, idx) => (
          <ArtifactButton
            key={idx}
            primaryKey={primaryKey}
            artifact={`attachment-${idx}`}
            label={`Pièce ${idx + 1}`}
            enabled
          />
        ))}
      </div>
    </details>
  );
}
